/**
 * CatastroPage.jsx — Catastro de Entidades
 * Unifica Clientes, Proveedores y Empleados en un solo módulo.
 * Cada entidad puede tener uno o más roles (checkboxes).
 */
import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, UserCheck, Briefcase, Building2, Plus, Search,
  Edit2, Trash2, ChevronDown, ChevronUp, X, AlertTriangle,
  ToggleLeft, ToggleRight, Settings, ClipboardList, ArrowLeft,
  Globe, MapPin, Edit, Save, ChevronRight, Cake, RefreshCw,
  UserX, Printer,
} from "lucide-react";
import { AuthContext } from "../App";
import ConstanciasRetencionBlock from "../components/ConstanciasRetencionBlock";
import CanalPagoGastoFields, { CANAL_PAGO_VACIO, canalPagoDe } from "../components/CanalPagoGastoFields";
import { AuditoriaRegistroBtn } from "../components/AuditoriaModal";
import { empresaTieneModulo } from "../lib/modulosEmpresa";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARGOS = ["Técnico IT","Desarrollador","Soporte","Administrador","Gerente","Contador","Recepcionista","Ventas","Otro"];
const MONEDAS = ["PYG","USD","BRL","ARS"];
const SUELDO_MINIMO_FALLBACK = 2899048;

const TIPOS_CONTRIBUYENTE = [
  { val: "juridica",        label: "Persona Jurídica",  desc: "Contribuyente IVA con RUC" },
  { val: "fisica",          label: "Persona Física",    desc: "CI/RUC — empleados e individuos" },
  { val: "extranjero",      label: "Extranjero",        desc: "Sin RUC paraguayo" },
  { val: "no_contribuyente",label: "No contribuyente",  desc: "Sin RUC — no deduce IVA" },
];

const FORM_EMPTY = {
  nombre: "", razon_social: "", apellido: "", ruc: "", tipo_documento: "", telefono: "", email: "",
  direccion: "", ciudad: "", notas: "",
  activo: true,
  tipo_contribuyente: "juridica",
  es_cliente: false, es_proveedor: false, es_empleado: false, es_chofer: false, es_vendedor: false, es_repartidor: false, es_despachante: false, porcentaje_comision: "",
  es_entidad_bancaria: false,
  // chofer (Transporte): licencia + comisión sobre el total del flete
  nro_licencia: "", vencimiento_licencia: "", tipo_comision_chofer: "", valor_comision_chofer: "",
  // canal de pago habitual (proveedores) — usado en Programación de Caja
  ...CANAL_PAGO_VACIO,
  // datos personales
  fecha_nacimiento: "", sexo: "", recordatorio: "",
  // retención IVA (clientes)
  aplica_retencion: false, porcentaje_retencion: "",
  // empleado
  cargo: "", fecha_ingreso: "", fecha_egreso: "",
  sueldo_base: "", moneda_sueldo: "PYG", tipo_cambio: "",
  aplica_ips: false, base_calculo_ips: "minimo",
  sueldo_minimo_vigente: SUELDO_MINIMO_FALLBACK, ips_monto_manual: "",
  // localidad
  pais: "", municipio: "", barrio: "", nacionalidad: "",
  c_pais: "PRY", ddes_pais: "Paraguay",
  c_dep: "01",   ddes_dep: "Capital",
  c_dis: "1",    ddes_dis: "Asuncion (Distrito)",
  c_loc: "1",    ddes_loc: "Asuncion (Distrito)",
};

/** Devuelve los días hasta el próximo cumpleaños (0 = hoy, negativo = ya pasó) */
function diasHastaCumpleanos(fechaNacimiento) {
  if (!fechaNacimiento) return null;
  try {
    const fn = new Date(fechaNacimiento);
    const hoy = new Date();
    let prox = new Date(hoy.getFullYear(), fn.getMonth(), fn.getDate());
    if (prox < hoy) prox = new Date(hoy.getFullYear() + 1, fn.getMonth(), fn.getDate());
    return Math.round((prox - hoy) / 86400000);
  } catch { return null; }
}

const TABS = [
  { key: "todos",      label: "Todos",       icon: ClipboardList,  filter: {} },
  { key: "clientes",   label: "Clientes",    icon: Building2,      filter: { es_cliente: true } },
  { key: "proveedores",label: "Proveedores", icon: Briefcase,      filter: { es_proveedor: true } },
  { key: "empleados",  label: "Empleados",   icon: UserCheck,      filter: { es_empleado: true } },
];

const ROLE_OPTIONS = [
  ["es_cliente", "Cliente", "blue", "empresas.ver"],
  ["es_proveedor", "Proveedor", "green", "proveedores.ver"],
  ["es_empleado", "Empleado", "violet", "empleados.ver"],
  // Solo aparece si la empresa tiene habilitado el módulo Transporte —
  // hasPermission ya filtra por módulo de empresa, no hace falta chequeo extra.
  ["es_chofer", "Chofer", "amber", "transporte.ver"],
  ["es_vendedor", "Vendedor", "cyan", "vendedores.ver"],
  ["es_repartidor", "Repartidor", "orange", "facturas.imprimir_delivery"],
  // Solo aparece si la empresa tiene habilitado el módulo Importaciones.
  ["es_despachante", "Despachante", "rose", "importaciones.ver"],
];

function badge(label, color) {
  const cls = {
    blue:   "bg-blue-500/15 text-blue-300 border-blue-500/30",
    green:  "bg-green-500/15 text-green-300 border-green-500/30",
    violet: "bg-violet-500/15 text-violet-300 border-violet-500/30",
    cyan:   "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
    red:    "bg-red-500/15 text-red-300 border-red-500/30",
    amber:  "bg-amber-500/15 text-amber-300 border-amber-500/30",
    orange: "bg-orange-500/15 text-orange-300 border-orange-500/30",
    rose:   "bg-rose-500/15 text-rose-300 border-rose-500/30",
  }[color] || "bg-white/10 text-slate-300 border-white/20";
  return (
    <span key={label} className={`px-1.5 py-0.5 rounded text-[10px] border font-medium ${cls}`}>{label}</span>
  );
}

function hoyISO() {
  return new Date().toISOString().slice(0, 10);
}

function escapeHtml(value = "") {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatoMontoLiquidacion(value, moneda = "PYG") {
  const n = Number(value || 0);
  if (moneda === "USD") return `$ ${n.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (moneda === "BRL") return `R$ ${n.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `₲ ${Math.round(n).toLocaleString("es-PY")}`;
}

async function readApiError(res, fallback) {
  const data = await res.json().catch(() => null);
  const detail = data?.detail;
  if (Array.isArray(detail)) {
    return detail.map(d => d?.msg || d?.message || JSON.stringify(d)).join("; ") || fallback;
  }
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object") return detail.message || JSON.stringify(detail);
  return fallback || `Error ${res.status}`;
}

function imprimirLiquidacionEmpleado(ent, data, currentUser, logoActual) {
  if (!ent) return;
  const moneda = ent.moneda_liquidacion || ent.moneda_sueldo || ent.moneda || "PYG";
  const nombre = [ent.nombre, ent.apellido].filter(Boolean).join(" ");
  const monto = formatoMontoLiquidacion(data?.monto_liquidacion, moneda);
  const html = `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8"/>
        <title>Liquidación - ${escapeHtml(nombre)}</title>
        <style>
          @page { size: A4; margin: 18mm; }
          body { font-family: Arial, sans-serif; color: #111827; margin: 0; }
          .doc { max-width: 780px; margin: 0 auto; }
          .top { display: flex; justify-content: space-between; gap: 24px; border-bottom: 2px solid #111827; padding-bottom: 16px; }
          .brand { font-size: 14px; color: #4b5563; }
          h1 { font-size: 20px; margin: 20px 0 6px; text-align: center; letter-spacing: .04em; }
          .subtitle { text-align: center; color: #6b7280; margin-bottom: 22px; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 18px; margin: 18px 0; }
          .field { border: 1px solid #d1d5db; border-radius: 8px; padding: 10px 12px; min-height: 42px; }
          .label { display: block; color: #6b7280; font-size: 11px; text-transform: uppercase; margin-bottom: 4px; }
          .value { font-size: 14px; font-weight: 700; }
          .amount { background: #ecfdf5; border-color: #86efac; color: #047857; font-size: 22px; }
          .notes { min-height: 88px; white-space: pre-wrap; }
          .text { line-height: 1.55; margin-top: 18px; font-size: 14px; }
          .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 72px; margin-top: 90px; text-align: center; }
          .line { border-top: 1px solid #111827; padding-top: 8px; font-size: 13px; }
          .small { color: #6b7280; font-size: 11px; margin-top: 4px; }
          @media print { .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="doc">
          <div class="top">
            <div>
              <div class="brand">Empresa</div>
              <strong>${escapeHtml(logoActual || ent.logo_tipo || "-")}</strong>
            </div>
            <div>
              <div class="brand">Fecha de emisión</div>
              <strong>${escapeHtml(hoyISO())}</strong>
            </div>
          </div>
          <h1>DOCUMENTO DE LIQUIDACIÓN LABORAL</h1>
          <div class="subtitle">Liquidación / desvinculación de empleado</div>
          <div class="grid">
            <div class="field"><span class="label">Empleado</span><span class="value">${escapeHtml(nombre || "-")}</span></div>
            <div class="field"><span class="label">RUC / CI</span><span class="value">${escapeHtml(ent.ruc || "-")}</span></div>
            <div class="field"><span class="label">Cargo</span><span class="value">${escapeHtml(ent.cargo || "-")}</span></div>
            <div class="field"><span class="label">Fecha de ingreso</span><span class="value">${escapeHtml(ent.fecha_ingreso || "-")}</span></div>
            <div class="field"><span class="label">Fecha de egreso</span><span class="value">${escapeHtml(data?.fecha_egreso || "-")}</span></div>
            <div class="field"><span class="label">Registrado por</span><span class="value">${escapeHtml(currentUser?.name || currentUser?.email || "-")}</span></div>
            <div class="field amount" style="grid-column: 1 / -1;"><span class="label">Monto de liquidación</span><span class="value">${escapeHtml(monto)}</span></div>
            <div class="field" style="grid-column: 1 / -1;"><span class="label">Motivo</span><span class="value">${escapeHtml(data?.motivo || "Liquidación / desvinculación")}</span></div>
            <div class="field notes" style="grid-column: 1 / -1;"><span class="label">Notas</span>${escapeHtml(data?.notas || "")}</div>
          </div>
          <p class="text">
            Por medio del presente documento se deja constancia de la liquidación laboral correspondiente al empleado indicado,
            por el monto señalado, quedando sujeto a la conformidad de ambas partes y a los documentos respaldatorios internos.
          </p>
          <div class="signatures">
            <div>
              <div class="line">Firma del empleador</div>
              <div class="small">Aclaración</div>
            </div>
            <div>
              <div class="line">Firma del empleado</div>
              <div class="small">C.I.N°</div>
            </div>
          </div>
        </div>
        <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 400); };</script>
      </body>
    </html>`;
  const win = window.open("", "_blank", "width=900,height=700");
  if (!win) {
    toast.error("El navegador bloqueó la ventana de impresión");
    return;
  }
  win.document.write(html);
  win.document.close();
}

// ── Sección de Localización (cascade selectors) ───────────────────────────────
function LocalizacionSection({ form, set, token }) {
  const hdr = { Authorization: `Bearer ${token}` };
  const [paises, setPaises]           = useState([]);
  const [departamentos, setDeps]      = useState([]);
  const [ciudades, setCiudades]       = useState([]);
  const [localidades, setLocalidades] = useState([]);

  useEffect(() => {
    fetch(`${API}/admin/localidades/paises`, { headers: hdr })
      .then(r => r.ok ? r.json() : []).then(setPaises).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!form.c_pais) { setDeps([]); return; }
    fetch(`${API}/admin/localidades/${form.c_pais}/departamentos`, { headers: hdr })
      .then(r => r.ok ? r.json() : []).then(setDeps).catch(() => {});
  }, [form.c_pais]); // eslint-disable-line

  useEffect(() => {
    if (!form.c_pais || !form.c_dep) { setCiudades([]); return; }
    fetch(`${API}/admin/localidades/${form.c_pais}/departamentos/${form.c_dep}/ciudades`, { headers: hdr })
      .then(r => r.ok ? r.json() : []).then(setCiudades).catch(() => {});
  }, [form.c_pais, form.c_dep]); // eslint-disable-line

  useEffect(() => {
    if (!form.c_pais || !form.c_dep || !form.c_dis) { setLocalidades([]); return; }
    fetch(`${API}/admin/localidades/${form.c_pais}/departamentos/${form.c_dep}/ciudades/${form.c_dis}/localidades`, { headers: hdr })
      .then(r => r.ok ? r.json() : []).then(setLocalidades).catch(() => {});
  }, [form.c_pais, form.c_dep, form.c_dis]); // eslint-disable-line

  const inp = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500/60 placeholder-slate-500";
  const sel = "w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500/60 disabled:opacity-40";
  const lbl = "text-slate-400 text-xs block mb-1";

  return (
    <div className="bg-green-900/10 border border-green-500/20 rounded-xl p-4 space-y-3">
      <p className="text-green-300 text-xs font-medium">Localización</p>

      {/* País + Nacionalidad */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={lbl}>País</label>
          <select value={form.c_pais || ""} className={sel} onChange={e => {
            const p = paises.find(x => x.cPais === e.target.value);
            set("c_pais", e.target.value); set("ddes_pais", p?.dDesPais || "");
            set("c_dep",""); set("ddes_dep",""); set("c_dis",""); set("ddes_dis",""); set("c_loc",""); set("ddes_loc","");
          }}>
            <option value="">— País —</option>
            {paises.map(p => <option key={p.cPais} value={p.cPais}>{p.dDesPais}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl}>Nacionalidad</label>
          <input value={form.nacionalidad || ""} onChange={e => set("nacionalidad", e.target.value)}
            placeholder="Paraguayo/a" className={inp}/>
        </div>
      </div>

      {/* Departamento */}
      <div>
        <label className={lbl}>Departamento</label>
        <select value={form.c_dep || ""} className={sel} disabled={!form.c_pais} onChange={e => {
          const d = departamentos.find(x => x.cDep === e.target.value);
          set("c_dep", e.target.value); set("ddes_dep", d?.dDesDep || "");
          set("c_dis",""); set("ddes_dis",""); set("c_loc",""); set("ddes_loc","");
        }}>
          <option value="">— Departamento —</option>
          {departamentos.map(d => <option key={d.cDep} value={d.cDep}>{d.dDesDep}</option>)}
        </select>
      </div>

      {/* Distrito/Ciudad */}
      <div>
        <label className={lbl}>Distrito / Municipio</label>
        <select value={form.c_dis || ""} className={sel} disabled={!form.c_dep} onChange={e => {
          const c = ciudades.find(x => String(x.cCiu) === e.target.value);
          set("c_dis", e.target.value); set("ddes_dis", c?.dDesCiu || "");
          set("c_loc",""); set("ddes_loc","");
        }}>
          <option value="">— Distrito —</option>
          {ciudades.map(c => <option key={c.cCiu} value={String(c.cCiu)}>{c.dDesCiu}</option>)}
        </select>
      </div>

      {/* Localidad + Barrio */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={lbl}>Localidad</label>
          <select value={form.c_loc || ""} className={sel} disabled={!form.c_dis} onChange={e => {
            const l = localidades.find(x => String(x.cLoc) === e.target.value);
            set("c_loc", e.target.value); set("ddes_loc", l?.dDesLoc || "");
          }}>
            <option value="">— Localidad —</option>
            {localidades.map(l => <option key={l.cLoc} value={String(l.cLoc)}>{l.dDesLoc}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl}>Barrio</label>
          <input value={form.barrio || ""} onChange={e => set("barrio", e.target.value)}
            placeholder="Barrio" className={inp}/>
        </div>
      </div>
    </div>
  );
}


// ── Modal de entidad ──────────────────────────────────────────────────────────
/**
 * HuellaDigitalField — huella digital de la entidad (dato biométrico sensible).
 * Permisos: entidades.ver_huella (ver) / entidades.cargar_huella (cargar/eliminar).
 * El backend decide: si responde 403 al consultar, el bloque no se muestra.
 * Captura: (a) lector USB con servicio local SecuGen WebAPI (https://localhost:8443),
 * (b) subir/pegar imagen escaneada. Los lectores integrados de notebook HP / Mac
 * (Windows Hello / Touch ID) NO entregan datos de huella a una web: solo autentican.
 */
const getPath = (obj, path) => (path || "").split(".").reduce((o, k) => (o == null ? o : o[k]), obj);

// Llama al servicio local del lector elegido para la empresa (config_huella).
async function capturarDesdeServicioLocal(cfg, dedoId) {
  const base = { Timeout: "10000", Quality: "50", licstr: "", templateFormat: "ISO", imageWID: "300", imageHGT: "400" };
  let extra = {};
  try { extra = cfg.parametros ? JSON.parse(cfg.parametros) : {}; } catch { extra = {}; }
  const params = cfg.adaptador === "secugen" ? { ...base, ...extra } : { ...extra };
  let resp;
  if ((cfg.metodo || "POST") === "GET") {
    const qs = new URLSearchParams(params).toString();
    resp = await fetch(qs ? `${cfg.url}${cfg.url.includes("?") ? "&" : "?"}${qs}` : cfg.url);
  } else if (cfg.formato_envio === "json") {
    resp = await fetch(cfg.url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(params) });
  } else {
    resp = await fetch(cfg.url, { method: "POST", body: new URLSearchParams(params) });
  }
  const d = await resp.json();
  const err = getPath(d, cfg.campo_error);
  if (err !== undefined && err !== null && String(err) !== "0" && String(err) !== "") {
    throw new Error(`El lector respondió con error (${err}). Apoyá el dedo y reintentá.`);
  }
  const img = getPath(d, cfg.campo_imagen);
  if (!img) throw new Error("El lector no devolvió imagen. Revisá el campo de imagen en Configuración.");
  const imagen = String(img).startsWith("data:") ? img : `data:${cfg.mime_imagen || "image/bmp"};base64,${img}`;
  const template = getPath(d, cfg.campo_template) || null;
  return { imagen, template };
}

function HuellaDigitalField({ entidadId, token, logo }) {
  const [estado, setEstado] = useState("cargando"); // cargando | sin_permiso | listo
  const [cfg, setCfg] = useState(null);
  const [huella, setHuella] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verImagen, setVerImagen] = useState(false);
  const [dedo, setDedo] = useState("indice_derecho");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(`${API}/admin/entidades/${entidadId}/huella`, { headers });
        if (!vivo) return;
        if (r.status === 403) { setEstado("sin_permiso"); return; }
        const d = r.ok ? await r.json() : null;
        setHuella(d && d.imagen ? d : null);
        setEstado("listo");
        try {
          const rc = await fetch(`${API}/admin/config-huella${logo ? `?logo_tipo=${logo}` : ""}`, { headers });
          if (vivo && rc.ok) setCfg(await rc.json());
        } catch { /* sin config: solo imagen manual */ }
      } catch { if (vivo) setEstado("sin_permiso"); }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line
  }, [entidadId]);

  if (estado === "cargando" || estado === "sin_permiso") return null;

  const guardar = async (imagen, extra = {}) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/entidades/${entidadId}/huella`, {
        method: "PUT", headers, body: JSON.stringify({ imagen, dedo, ...extra }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "No se pudo guardar la huella (¿tenés el permiso 'cargar huella'?)"); return; }
      setHuella(d); setVerImagen(true);
      toast.success("Huella digital guardada");
    } finally { setBusy(false); }
  };

  const onArchivo = (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|bmp)$/.test(file.type)) { toast.error("Usá una imagen PNG, JPG o BMP"); return; }
    const fr = new FileReader();
    fr.onload = () => guardar(fr.result, { origen: "imagen" });
    fr.readAsDataURL(file);
  };

  const onPegar = (e) => {
    const it = [...(e.clipboardData?.items || [])].find(i => i.type.startsWith("image/"));
    if (it) { e.preventDefault(); onArchivo(it.getAsFile()); }
  };

  const hayLector = !!cfg && (cfg.adaptador === "secugen" || cfg.adaptador === "http") && !!cfg.url;

  const capturarLector = async () => {
    if (!hayLector) return;
    setBusy(true);
    let res;
    try {
      res = await capturarDesdeServicioLocal(cfg);
    } catch (e) {
      toast.error(e.message?.startsWith("El lector")
        ? e.message
        : `No se pudo conectar con el servicio local de ${cfg.dispositivo_nombre}. Instalalo/iniciá el servicio en esta PC y conectá el lector USB.`);
      setBusy(false);
      return;
    }
    setBusy(false);
    await guardar(res.imagen, {
      origen: `lector_${cfg.dispositivo}`, template: res.template, formato_template: res.template ? "ISO19794-2" : null,
    });
  };

  const eliminar = async () => {
    if (!window.confirm("¿Eliminar la huella digital registrada de esta entidad?")) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/entidades/${entidadId}/huella`, { method: "DELETE", headers });
      if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d.detail || "No se pudo eliminar"); return; }
      setHuella(null); setVerImagen(false);
      toast.success("Huella eliminada");
    } finally { setBusy(false); }
  };

  const btn = "px-2.5 py-1.5 rounded-lg text-xs border border-slate-600 text-slate-200 hover:bg-slate-700 disabled:opacity-50";
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/40 p-3 space-y-2" onPaste={onPegar}>
      <div className="flex items-center justify-between">
        <label className="text-slate-300 text-xs font-medium">Huella digital</label>
        <span className={`text-[11px] ${huella ? "text-emerald-400" : "text-slate-500"}`}>
          {huella ? `Registrada${huella.capturado_at ? " · " + new Date(huella.capturado_at).toLocaleDateString("es-PY") : ""}` : "Sin huella registrada"}
        </span>
      </div>

      {huella && (
        <div className="space-y-2">
          {verImagen
            ? <img src={huella.imagen} alt="Huella digital" className="max-h-48 rounded bg-white p-1" />
            : <button type="button" className={btn} onClick={() => setVerImagen(true)}>Mostrar huella</button>}
          <p className="text-slate-500 text-[11px]">
            {huella.dedo ? `Dedo: ${huella.dedo.replace("_", " ")} · ` : ""}Origen: {huella.origen || "imagen"}
            {huella.capturado_por ? ` · Cargada por ${huella.capturado_por}` : ""}
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <select value={dedo} onChange={e => setDedo(e.target.value)} className="bg-slate-900 border border-slate-600 rounded-lg text-xs text-slate-200 px-2 py-1.5">
          <option value="pulgar_derecho">Pulgar derecho</option>
          <option value="indice_derecho">Índice derecho</option>
          <option value="medio_derecho">Medio derecho</option>
          <option value="pulgar_izquierdo">Pulgar izquierdo</option>
          <option value="indice_izquierdo">Índice izquierdo</option>
          <option value="medio_izquierdo">Medio izquierdo</option>
        </select>
        {hayLector && (
          <button type="button" className={btn} disabled={busy} onClick={capturarLector}>
            Capturar con {cfg.dispositivo_nombre}
          </button>
        )}
        <label className={`${btn} cursor-pointer`}>
          Subir imagen
          <input type="file" accept="image/png,image/jpeg,image/bmp" className="hidden"
            onChange={e => { onArchivo(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        {huella && <button type="button" className={`${btn} !text-red-300 !border-red-800`} disabled={busy} onClick={eliminar}>Eliminar</button>}
      </div>
      <p className="text-slate-500 text-[11px]">
        {hayLector
          ? `Lector de la empresa: ${cfg.dispositivo_nombre}.`
          : "Esta empresa no tiene un lector configurado (Catastro > Configuración > Lector de huella): solo se puede subir imagen."}
        {" "}También podés subir o pegar (Ctrl+V) una imagen escaneada. Dato biométrico sensible: acceso restringido por permiso y auditado.
      </p>
    </div>
  );
}

export function EntidadModal({ entidad, onClose, onSaved, token, logo, allowedRoles }) {
  const isEdit = !!entidad?.id;
  const [form, setForm] = useState(() => {
    if (isEdit) return {
      nombre: entidad.nombre || "", razon_social: entidad.razon_social || entidad.nombre || "", apellido: entidad.apellido || "", ruc: entidad.ruc || "",
      tipo_documento: entidad.tipo_documento || "",
      telefono: entidad.telefono || "", email: entidad.email || "",
      direccion: entidad.direccion || "", ciudad: entidad.ciudad || "",
      notas: entidad.notas || "", activo: entidad.activo !== false,
      tipo_contribuyente: entidad.tipo_contribuyente || "juridica",
      es_cliente: !!entidad.es_cliente, es_proveedor: !!entidad.es_proveedor, es_empleado: !!entidad.es_empleado,
      es_chofer: !!entidad.es_chofer,
      es_vendedor: !!entidad.es_vendedor,
      es_repartidor: !!entidad.es_repartidor,
      es_despachante: !!entidad.es_despachante,
      porcentaje_comision: entidad.porcentaje_comision ?? "",
      nro_licencia: entidad.nro_licencia || "",
      vencimiento_licencia: entidad.vencimiento_licencia || "",
      tipo_comision_chofer: entidad.tipo_comision_chofer || "",
      valor_comision_chofer: entidad.valor_comision_chofer ?? "",
      es_entidad_bancaria: !!entidad.es_entidad_bancaria,
      ...canalPagoDe(entidad),
      // datos personales
      fecha_nacimiento: entidad.fecha_nacimiento || "",
      sexo: entidad.sexo || "",
      recordatorio: entidad.recordatorio || "",
      aplica_retencion: !!entidad.aplica_retencion,
      porcentaje_retencion: entidad.porcentaje_retencion != null ? String(entidad.porcentaje_retencion) : "",
      // empleado
      cargo: entidad.cargo || "", fecha_ingreso: entidad.fecha_ingreso || "", fecha_egreso: entidad.fecha_egreso || "",
      sueldo_base: entidad.sueldo_base ?? "", moneda_sueldo: entidad.moneda_sueldo || "PYG",
      tipo_cambio: entidad.tipo_cambio ?? "",
      aplica_ips: !!entidad.aplica_ips, base_calculo_ips: entidad.base_calculo_ips || "minimo",
      sueldo_minimo_vigente: entidad.sueldo_minimo_vigente ?? SUELDO_MINIMO_FALLBACK,
      ips_monto_manual: entidad.ips_monto_manual ?? "",
      pais: entidad.pais || "", municipio: entidad.municipio || "",
      barrio: entidad.barrio || "", nacionalidad: entidad.nacionalidad || "",
      c_pais: entidad.c_pais || "", ddes_pais: entidad.ddes_pais || "",
      c_dep:  entidad.c_dep  || "", ddes_dep:  entidad.ddes_dep  || "",
      c_dis:  entidad.c_dis  ? String(entidad.c_dis)  : "", ddes_dis:  entidad.ddes_dis  || "",
      c_loc:  entidad.c_loc  ? String(entidad.c_loc)  : "", ddes_loc:  entidad.ddes_loc  || "",
    };
    // Nuevo con rol pre-marcado (viene del dashboard con ?nuevo=cliente|proveedor|empleado)
    const presetRol = entidad?._presetRol;
    const fallbackRol = ROLE_OPTIONS.find(([k]) => allowedRoles?.[k])?.[0];
    return {
      ...FORM_EMPTY,
      ...(presetRol && allowedRoles?.[presetRol] ? { [presetRol]: true } : {}),
      ...(!presetRol && fallbackRol ? { [fallbackRol]: true } : {}),
      nombre: entidad?._initialNombre || "",
      razon_social: entidad?._initialRazonSocial || entidad?._initialNombre || "",
      ruc: entidad?._initialRuc || "",
      telefono: entidad?._initialTelefono || "",
      email: entidad?._initialEmail || "",
      // Si es empleado pre-marcado, cambiar tipo_contribuyente a física
      ...(presetRol === "es_empleado" ? { tipo_contribuyente: "fisica" } : {}),
    };
  });

  const [saving, setSaving] = useState(false);
  const [dupError, setDupError] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Sucursales: si la empresa comparte RRHH con su grupo, el empleado elige su empresa (contable/sueldo)
  const [grupoRrhh, setGrupoRrhh] = useState(null); // {empresas:[{slug,nombre}]} | null
  const empresaOriginal = isEdit ? (entidad.logo_tipo || logo || "") : (logo || "");
  const [empresaContable, setEmpresaContable] = useState(empresaOriginal);
  useEffect(() => {
    let vivo = true;
    fetch(`${API}/admin/grupos-empresas/mi-grupo?logo_tipo=${encodeURIComponent(empresaOriginal)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (vivo) setGrupoRrhh(d && (d.compartidos || []).includes("rrhh") && (d.empresas || []).length > 1 ? d : null); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [empresaOriginal, token]);
  const mostrarEmpresaContable = !!(form.es_empleado && grupoRrhh);

  const validate = () => {
    if (!form.nombre.trim()) { toast.error("El nombre es obligatorio"); return false; }
    if (!form.es_cliente && !form.es_proveedor && !form.es_empleado && !form.es_chofer && !form.es_vendedor && !form.es_repartidor && !form.es_despachante) {
      toast.error("Seleccioná al menos un rol (Cliente, Proveedor, Empleado, Chofer, Vendedor, Repartidor o Despachante)"); return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setDupError("");
    const url = isEdit
      ? `${API}/admin/entidades/${entidad.id}`
      : `${API}/admin/entidades${(mostrarEmpresaContable ? empresaContable : logo) ? `?logo_tipo=${mostrarEmpresaContable ? empresaContable : logo}` : ""}`;
    const method = isEdit ? "PUT" : "POST";
    const body = {
      ...form,
      es_entidad_bancaria: !!(form.es_proveedor && form.es_entidad_bancaria),
      sueldo_base: form.sueldo_base !== "" ? parseFloat(form.sueldo_base) : null,
      tipo_cambio: form.tipo_cambio !== "" ? parseFloat(form.tipo_cambio) : null,
      ips_monto_manual: form.ips_monto_manual !== "" ? parseFloat(form.ips_monto_manual) : null,
      sueldo_minimo_vigente: parseFloat(form.sueldo_minimo_vigente) || SUELDO_MINIMO_FALLBACK,
      porcentaje_retencion: form.porcentaje_retencion !== "" ? parseFloat(form.porcentaje_retencion) : null,
      fecha_nacimiento: form.fecha_nacimiento || null,
      sexo: form.sexo || null,
      recordatorio: form.recordatorio || null,
      es_vendedor: !!form.es_vendedor,
      es_repartidor: !!form.es_repartidor,
      es_despachante: !!form.es_despachante,
      porcentaje_comision: form.es_vendedor && form.porcentaje_comision !== "" && form.porcentaje_comision != null ? parseFloat(form.porcentaje_comision) : null,
      nro_licencia: form.es_chofer ? (form.nro_licencia || null) : null,
      vencimiento_licencia: form.es_chofer ? (form.vencimiento_licencia || null) : null,
      tipo_comision_chofer: form.es_chofer && form.tipo_comision_chofer ? form.tipo_comision_chofer : null,
      valor_comision_chofer: form.es_chofer && form.valor_comision_chofer !== "" && form.valor_comision_chofer != null ? parseFloat(form.valor_comision_chofer) : null,
    };
    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const saved = await res.json().catch(() => null);
        if (isEdit && mostrarEmpresaContable && empresaContable && empresaContable !== empresaOriginal) {
          const r2 = await fetch(`${API}/admin/empleados/${entidad.id}/empresa-contable`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ logo_tipo: empresaContable }),
          });
          if (!r2.ok) {
            const e2 = await r2.json().catch(() => ({}));
            toast.error(e2.detail || "Se guardó el empleado pero no se pudo cambiar la empresa");
          } else {
            toast.success("Empresa del empleado actualizada: los próximos sueldos van a la nueva empresa");
          }
        }
        toast.success(isEdit ? "Entidad actualizada" : "Entidad creada");
        onSaved(saved);
      } else {
        const err = await res.json().catch(() => ({}));
        const msg = err.detail || "Error al guardar";
        if (res.status === 409) setDupError(msg);
        else toast.error(msg);
      }
    } catch { toast.error("Error de conexión"); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-500/60 placeholder-slate-500";
  const selCls  = "w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-500/60";

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-arandu-dark-light z-10">
          <h3 className="text-white font-semibold">{isEdit ? "Editar entidad" : "Nueva entidad"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5"/></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Roles */}
          <div>
            <label className="text-slate-400 text-xs block mb-2">Roles *</label>
            <div className="flex flex-wrap gap-3">
              {ROLE_OPTIONS.filter(([k]) => allowedRoles?.[k] || form[k]).map(([k,l,c]) => (
                <button key={k} type="button"
                  onClick={() => {
                    const newVal = !form[k];
                    if (k === "es_empleado" && newVal && form.tipo_contribuyente === "juridica") {
                      setForm(f => ({ ...f, [k]: newVal, tipo_contribuyente: "fisica" }));
                    } else if (k === "es_proveedor" && !newVal) {
                      setForm(f => ({ ...f, [k]: newVal, es_entidad_bancaria: false }));
                    } else {
                      set(k, newVal);
                    }
                  }}
                  className={`flex-1 min-w-[100px] py-2 rounded-lg text-xs font-medium border transition-all ${
                    form[k]
                      ? c==="blue"   ? "bg-blue-600/30 border-blue-500/60 text-blue-300"
                      : c==="green"  ? "bg-green-600/30 border-green-500/60 text-green-300"
                      :               "bg-violet-600/30 border-violet-500/60 text-violet-300"
                      : "border-white/10 text-slate-400 hover:text-white bg-white/5"
                  }`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {form.es_proveedor && (
            <label className="flex items-start gap-3 p-3 rounded-xl border border-cyan-500/25 bg-cyan-500/10 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.es_entidad_bancaria}
                onChange={e => set("es_entidad_bancaria", e.target.checked)}
                className="mt-1"
              />
              <span>
                <span className="block text-cyan-200 text-sm font-medium">Entidad bancaria / financiera</span>
                <span className="block text-cyan-100/70 text-xs mt-0.5">
                  Usar este proveedor para préstamos bancarios y reportes financieros.
                </span>
              </span>
            </label>
          )}

          {(form.es_proveedor || form.es_cliente || form.es_empleado) && (
            <CanalPagoGastoFields value={form} onChange={(patch) => setForm(prev => ({ ...prev, ...patch }))} />
          )}

          {isEdit && <HuellaDigitalField entidadId={entidad.id} token={token} logo={entidad.logo_tipo || logo} />}

          {/* Tipo de contribuyente */}
          <div>
            <label className="text-slate-400 text-xs block mb-2">Tipo de contribuyente</label>
            <div className="grid grid-cols-2 gap-2">
              {TIPOS_CONTRIBUYENTE.map(({ val, label, desc }) => (
                <button key={val} type="button"
                  onClick={() => set("tipo_contribuyente", val)}
                  className={`p-2.5 rounded-lg border text-left transition-all ${
                    form.tipo_contribuyente === val
                      ? "bg-amber-600/20 border-amber-500/50 text-amber-200"
                      : "border-white/10 text-slate-400 hover:text-white bg-white/5"
                  }`}>
                  <div className="text-xs font-medium">{label}</div>
                  <div className="text-[10px] opacity-60 mt-0.5">{desc}</div>
                </button>
              ))}
            </div>
          </div>

          {mostrarEmpresaContable && (
            <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-3">
              <label className="text-violet-300 text-xs block mb-1">Empresa del empleado (contabilidad y sueldo)</label>
              <select value={empresaContable} onChange={e => setEmpresaContable(e.target.value)} className={inputCls}>
                {grupoRrhh.empresas.map(e => <option key={e.slug} value={e.slug}>{e.nombre}</option>)}
              </select>
              <p className="text-[11px] text-slate-400 mt-1">
                Los sueldos y su impacto contable se registran en esta empresa. Los pagos anteriores no cambian.
              </p>
            </div>
          )}

          {/* Datos generales */}
          {form.es_empleado ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-slate-400 text-xs block mb-1">Nombre *</label>
                <input value={form.nombre} onChange={e => set("nombre", e.target.value)}
                  placeholder="Juan" className={inputCls}/>
              </div>
              <div>
                <label className="text-slate-400 text-xs block mb-1">Apellido *</label>
                <input value={form.apellido} onChange={e => set("apellido", e.target.value)}
                  placeholder="Pérez" className={inputCls}/>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-slate-400 text-xs block mb-1">Nombre *</label>
                <input value={form.nombre} onChange={e => set("nombre", e.target.value)}
                  placeholder="Nombre común o alias" className={inputCls}/>
              </div>
              <div>
                <label className="text-slate-400 text-xs block mb-1">Razón social</label>
                <input value={form.razon_social} onChange={e => set("razon_social", e.target.value)}
                  placeholder="Nombre legal (ej: ARANDU INFORMATICA SRL)" className={inputCls}/>
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-slate-400 text-xs block mb-1">
                {form.tipo_contribuyente === "extranjero" || form.tipo_contribuyente === "no_contribuyente"
                  ? "RUC / CI (opcional)" : "RUC / CI"}
              </label>
              <input value={form.ruc} onChange={e => set("ruc", e.target.value)}
                placeholder={form.tipo_contribuyente === "extranjero" ? "Documento extranjero" : "80012345-6"}
                className={inputCls}/>
            </div>
            <div>
              <label className="text-slate-400 text-xs block mb-1">Tipo doc.</label>
              <select value={form.tipo_documento} onChange={e => set("tipo_documento", e.target.value)}
                className={inputCls}>
                <option value="">Auto</option>
                <option value="RUC">RUC</option>
                <option value="CI">CI</option>
              </select>
            </div>
            <div>
              <label className="text-slate-400 text-xs block mb-1">Teléfono</label>
              <input value={form.telefono} onChange={e => set("telefono", e.target.value)}
                placeholder="0981 123456" className={inputCls}/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-400 text-xs block mb-1">Email</label>
              <input type="email" value={form.email} onChange={e => set("email", e.target.value)}
                placeholder="correo@ejemplo.com" className={inputCls}/>
            </div>
            <div>
              <label className="text-slate-400 text-xs block mb-1">Ciudad</label>
              <input value={form.ciudad} onChange={e => set("ciudad", e.target.value)}
                placeholder="Asunción" className={inputCls}/>
            </div>
          </div>
          <div>
            <label className="text-slate-400 text-xs block mb-1">Dirección</label>
            <input value={form.direccion} onChange={e => set("direccion", e.target.value)}
              placeholder="Calle, número, barrio" className={inputCls}/>
          </div>

          {/* Localización */}
          <LocalizacionSection form={form} set={set} token={token}/>

          {/* Datos de vendedor */}
          {form.es_vendedor && (
            <div className="bg-cyan-900/15 border border-cyan-500/25 rounded-xl p-4 space-y-3">
              <p className="text-cyan-300 text-xs font-medium">Datos de Vendedor</p>
              <div className="w-1/2">
                <label className="text-slate-400 text-xs block mb-1">Comisión (%)</label>
                <input type="number" min="0" max="100" step="any" value={form.porcentaje_comision ?? ""}
                  onChange={e => set("porcentaje_comision", e.target.value)} className={inputCls}/>
              </div>
              <p className="text-slate-500 text-[11px]">Se elige en la factura de venta y se usa en el reporte de Comisiones de Vendedores.</p>
            </div>
          )}

          {/* Datos de chofer (Transporte) */}
          {form.es_chofer && (
            <div className="bg-amber-900/15 border border-amber-500/25 rounded-xl p-4 space-y-3">
              <p className="text-amber-300 text-xs font-medium">Datos de Chofer</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-400 text-xs block mb-1">N° de Licencia</label>
                  <input value={form.nro_licencia} onChange={e => set("nro_licencia", e.target.value)} className={inputCls}/>
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Vencimiento de Licencia</label>
                  <DateInput value={form.vencimiento_licencia || ""} onChange={e => set("vencimiento_licencia", e.target.value)} className={inputCls}/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Comisión por carga</label>
                  <select value={form.tipo_comision_chofer || ""} onChange={e => set("tipo_comision_chofer", e.target.value)} className={selCls}>
                    <option value="">— Sin comisión —</option>
                    <option value="pct">% sobre el total del flete</option>
                    <option value="monto">Monto fijo por carga</option>
                  </select>
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">{form.tipo_comision_chofer === "pct" ? "Porcentaje (%)" : "Monto (₲)"}</label>
                  <input type="number" min="0" step="any" disabled={!form.tipo_comision_chofer} value={form.valor_comision_chofer ?? ""}
                    onChange={e => set("valor_comision_chofer", e.target.value)} className={inputCls}/>
                </div>
              </div>
              <p className="text-slate-500 text-[11px]">La comisión se calcula siempre sobre el total del flete de cada orden de carga.</p>
            </div>
          )}

          {/* Datos de empleado */}
          {form.es_empleado && (
            <div className="bg-violet-900/15 border border-violet-500/25 rounded-xl p-4 space-y-3">
              <p className="text-violet-300 text-xs font-medium">Datos de Empleado</p>
              <div>
                <label className="text-slate-400 text-xs block mb-1">Cargo</label>
                <select value={form.cargo} onChange={e => set("cargo", e.target.value)} className={selCls}>
                  <option value="">— Sin cargo —</option>
                  {CARGOS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Fecha de ingreso</label>
                  <DateInput value={form.fecha_ingreso} onChange={e => set("fecha_ingreso", e.target.value)} className={inputCls}/>
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Fecha de egreso</label>
                  <DateInput value={form.fecha_egreso} onChange={e => set("fecha_egreso", e.target.value)} className={inputCls}/>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-slate-400 text-xs block mb-1">Sueldo base</label>
                  <input type="number" value={form.sueldo_base} onChange={e => set("sueldo_base", e.target.value)}
                    placeholder="0" className={inputCls}/>
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Moneda</label>
                  <select value={form.moneda_sueldo} onChange={e => set("moneda_sueldo", e.target.value)} className={selCls}>
                    {MONEDAS.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              {form.moneda_sueldo !== "PYG" && (
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Tipo de cambio (a PYG)</label>
                  <input type="number" value={form.tipo_cambio} onChange={e => set("tipo_cambio", e.target.value)}
                    placeholder="7500" className={inputCls}/>
                </div>
              )}
              {/* IPS */}
              <div className="bg-blue-900/20 border border-blue-500/25 rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-blue-300 text-xs font-medium">Configuración IPS</span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-slate-400 text-xs">Aplica IPS</span>
                    <input type="checkbox" checked={form.aplica_ips}
                      onChange={e => set("aplica_ips", e.target.checked)} className="w-4 h-4 accent-blue-500"/>
                  </label>
                </div>
                {form.aplica_ips && (
                  <>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Base de cálculo</label>
                      <div className="flex gap-1.5">
                        {[["minimo","Mín. vigente"],["sueldo","Sueldo (9%)"],["manual","Base manual"]].map(([v,l]) => (
                          <button key={v} type="button" onClick={() => set("base_calculo_ips", v)}
                            className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                              form.base_calculo_ips === v
                                ? "bg-blue-600 border-blue-600 text-white"
                                : "border-white/10 text-slate-400 hover:text-white bg-white/5"
                            }`}>{l}</button>
                        ))}
                      </div>
                    </div>
                    {form.base_calculo_ips === "minimo" && (
                      <div>
                        <label className="text-slate-400 text-xs block mb-1">Salario mínimo vigente</label>
                        <input type="number" value={form.sueldo_minimo_vigente}
                          onChange={e => set("sueldo_minimo_vigente", parseFloat(e.target.value) || SUELDO_MINIMO_FALLBACK)}
                          className="w-full bg-white/5 border border-blue-500/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"/>
                      </div>
                    )}
                    {form.base_calculo_ips === "manual" && (
                      <div>
                        <label className="text-slate-400 text-xs block mb-1">Base manual (9%)</label>
                        <input type="number" value={form.ips_monto_manual}
                          onChange={e => set("ips_monto_manual", e.target.value)} placeholder="0"
                          className="w-full bg-white/5 border border-blue-500/30 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"/>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="text-slate-400 text-xs block mb-1">Notas</label>
            <textarea value={form.notas} onChange={e => set("notas", e.target.value)} rows={2}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none resize-none placeholder-slate-500"
              placeholder="Observaciones internas..."/>
          </div>

          {/* Datos personales: Fecha de nacimiento + Sexo */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-400 text-xs block mb-1">Fecha de nacimiento</label>
              <DateInput value={form.fecha_nacimiento} onChange={e => set("fecha_nacimiento", e.target.value)} className={inputCls}/>
            </div>
            <div>
              <label className="text-slate-400 text-xs block mb-1">Sexo</label>
              <select value={form.sexo} onChange={e => set("sexo", e.target.value)} className={selCls}>
                <option value="">— Sin especificar —</option>
                <option value="M">Masculino</option>
                <option value="F">Femenino</option>
              </select>
            </div>
          </div>

          {/* Recordatorio para el facturista */}
          <div>
            <label className="text-slate-400 text-xs block mb-1">Recordatorio <span className="text-slate-600">(visible al facturar)</span></label>
            <input value={form.recordatorio} onChange={e => set("recordatorio", e.target.value)}
              placeholder="Ej: Pedir RUC actualizado, descuento especial, etc."
              className={inputCls}/>
          </div>

          {/* Retención de IVA — solo clientes */}
          {form.es_cliente && (
            <div className="bg-amber-900/15 border border-amber-500/25 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-amber-300 text-xs font-medium">Retención de IVA</p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-slate-400 text-xs">Aplica retención</span>
                  <input type="checkbox" checked={form.aplica_retencion}
                    onChange={e => set("aplica_retencion", e.target.checked)} className="w-4 h-4 accent-amber-500"/>
                </label>
              </div>
              {form.aplica_retencion && (
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Porcentaje de retención (%)</label>
                  <input type="number" min="0" max="100" step="0.01"
                    value={form.porcentaje_retencion} onChange={e => set("porcentaje_retencion", e.target.value)}
                    placeholder="Ej: 30" className={inputCls}/>
                  <p className="text-slate-600 text-[10px] mt-1">Se aplicará automáticamente en el cálculo del balance</p>
                </div>
              )}
            </div>
          )}

          {/* Constancias de no retención — solo proveedores ya guardados */}
          {isEdit && form.es_proveedor && (
            <ConstanciasRetencionBlock entidad={entidad} token={token} />
          )}

          <div className="flex items-center gap-2">
            <input type="checkbox" id="activo-ck" checked={form.activo}
              onChange={e => set("activo", e.target.checked)} className="w-4 h-4 accent-violet-500"/>
            <label htmlFor="activo-ck" className="text-slate-300 text-sm cursor-pointer">Activo</label>
          </div>

          {dupError && (
            <div className="flex items-start gap-2 bg-red-900/20 border border-red-500/40 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0"/>
              <p className="text-red-300 text-xs">{dupError}</p>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg text-sm">
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex-1 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
              {saving ? "Guardando..." : isEdit ? "Actualizar" : "Crear entidad"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Config Sueldo Mínimo ──────────────────────────────────────────────────────
/** Configuración > Lector de huella digital: cada empresa elige su dispositivo. */
function HuellaConfigPanel({ token, logo, canEdit }) {
  const [catalogo, setCatalogo] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const q = logo ? `?logo_tipo=${logo}` : "";

  useEffect(() => {
    (async () => {
      try {
        const [rc, rf] = await Promise.all([
          fetch(`${API}/admin/config-huella/catalogo`, { headers }),
          fetch(`${API}/admin/config-huella${q}`, { headers }),
        ]);
        if (rc.ok) setCatalogo(await rc.json());
        if (rf.ok) setForm(await rf.json());
      } catch { toast.error("No se pudo cargar la configuración del lector"); }
    })();
    // eslint-disable-next-line
  }, [logo]);

  if (!form) return <p className="text-slate-400 text-sm">Cargando…</p>;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const disp = catalogo.find(d => d.id === form.dispositivo);
  const marcas = [...new Set(catalogo.map(d => d.marca))];
  const inputCls = "w-full bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200 px-3 py-2 disabled:opacity-60";

  const elegir = (id) => {
    const d = catalogo.find(x => x.id === id);
    setForm(f => ({ ...f, dispositivo: id, url: d?.url || (d?.adaptador === "http" ? f.url : "") }));
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/config-huella`, { method: "PUT", headers, body: JSON.stringify({ ...form, logo_tipo: logo }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "No se pudo guardar"); return; }
      setForm(d); toast.success("Lector de huella guardado para esta empresa");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-slate-400 text-xs block mb-1">Dispositivo de esta empresa</label>
        <select value={form.dispositivo} disabled={!canEdit} onChange={e => elegir(e.target.value)} className={inputCls}>
          {marcas.map(m => (
            <optgroup key={m} label={m === "—" ? "General" : m}>
              {catalogo.filter(d => d.marca === m).map(d => (
                <option key={d.id} value={d.id}>
                  {d.nombre}{d.recomendado ? " ★ recomendado" : ""}{d.estado === "configurar" ? " (requiere configurar servicio)" : ""}{d.estado === "no_soportado" ? " (no soportado en web)" : ""}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        {disp && <p className={`text-[11px] mt-1 ${disp.estado === "no_soportado" ? "text-amber-400" : "text-slate-500"}`}>{disp.ayuda}</p>}
      </div>

      {disp && (disp.adaptador === "secugen" || disp.adaptador === "http") && (
        <div className="space-y-3 rounded-lg border border-white/10 p-3">
          <div>
            <label className="text-slate-400 text-xs block mb-1">URL del servicio local</label>
            <input value={form.url || ""} disabled={!canEdit} onChange={e => set("url", e.target.value)} className={inputCls}
              placeholder="https://localhost:8443/..." />
            <p className="text-slate-500 text-[11px] mt-1">Debe ser https:// o http://localhost / 127.0.0.1 (servicio instalado en la PC del operador).</p>
          </div>
          {disp.adaptador === "http" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Método</label>
                  <select value={form.metodo} disabled={!canEdit} onChange={e => set("metodo", e.target.value)} className={inputCls}>
                    <option>POST</option><option>GET</option>
                  </select>
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Formato de envío</label>
                  <select value={form.formato_envio} disabled={!canEdit} onChange={e => set("formato_envio", e.target.value)} className={inputCls}>
                    <option value="form">Formulario</option><option value="json">JSON</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-slate-400 text-xs block mb-1">Parámetros extra (JSON, opcional)</label>
                <input value={form.parametros || ""} disabled={!canEdit} onChange={e => set("parametros", e.target.value)} className={inputCls} placeholder='{"Timeout":"10000"}' />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Campo imagen</label>
                  <input value={form.campo_imagen || ""} disabled={!canEdit} onChange={e => set("campo_imagen", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Campo plantilla</label>
                  <input value={form.campo_template || ""} disabled={!canEdit} onChange={e => set("campo_template", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Campo error</label>
                  <input value={form.campo_error || ""} disabled={!canEdit} onChange={e => set("campo_error", e.target.value)} className={inputCls} />
                </div>
              </div>
              <p className="text-slate-500 text-[11px]">Nombres de los campos de la respuesta JSON del servicio (admiten ruta con puntos, ej. data.image). La imagen debe venir en base64.</p>
            </>
          )}
        </div>
      )}

      {canEdit && (
        <div className="flex justify-end">
          <button onClick={guardar} disabled={saving || disp?.estado === "no_soportado"}
            className="h-9 text-sm bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white px-4 rounded-lg">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      )}
    </div>
  );
}

function SueldoMinimoPanel({ token, logo }) {
  const [sueldoMinimo, setSueldoMinimo]   = useState(SUELDO_MINIMO_FALLBACK);
  const [historial, setHistorial]          = useState([]);
  const [nuevoMin, setNuevoMin]            = useState("");
  const [fechaVig, setFechaVig]            = useState("");
  const [saving, setSaving]                = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/empleados/config-sueldo-minimo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { const d = await res.json(); setSueldoMinimo(d.valor); setHistorial(d.historial || []); }
    } catch {}
  }, [token]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleActualizar = async () => {
    const val = parseFloat(nuevoMin);
    if (!val || val <= 0) { toast.error("Ingresá un monto válido"); return; }
    if (!fechaVig) { toast.error("Ingresá la fecha de vigencia"); return; }
    const msg = `¿Actualizar el mínimo a ₲ ${val.toLocaleString("es-PY")} desde ${fechaVig}?\nSe actualizará el IPS y el sueldo base de empleados que cobran el mínimo anterior.`;
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/empleados/actualizar-sueldo-minimo`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ nuevo_minimo: val, actualizar_sueldos_base: true, fecha_vigencia: fechaVig, logo_tipo: logo || null }),
      });
      if (res.ok) {
        const d = await res.json();
        setSueldoMinimo(val); setNuevoMin(""); setFechaVig("");
        await fetchConfig();
        toast.success(`Mínimo actualizado. IPS: ${d.empleados_ips_actualizados} emp | Sueldos: ${d.empleados_base_actualizados} emp.`);
      } else {
        const err = await res.json().catch(() => ({})); toast.error(err.detail || "Error");
      }
    } catch { toast.error("Error de conexión"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg space-y-4">
      <div className="bg-arandu-dark-light border border-white/10 rounded-xl p-4">
        <p className="text-slate-400 text-xs mb-1">Sueldo mínimo vigente</p>
        <p className="text-white text-2xl font-bold font-mono">₲ {sueldoMinimo.toLocaleString("es-PY")}</p>
      </div>
      <div className="bg-arandu-dark-light border border-white/10 rounded-xl p-4 space-y-3">
        <p className="text-slate-300 text-sm font-medium">Actualizar sueldo mínimo</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-slate-400 text-xs block mb-1">Nuevo monto (₲)</label>
            <input type="number" value={nuevoMin} onChange={e => setNuevoMin(e.target.value)}
              placeholder={`Ej: ${(sueldoMinimo * 1.08).toFixed(0)}`}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"/>
          </div>
          <div>
            <label className="text-slate-400 text-xs block mb-1">Fecha de vigencia</label>
            <DateInput value={fechaVig} onChange={e => setFechaVig(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"/>
          </div>
        </div>
        <button onClick={handleActualizar} disabled={saving}
          className="w-full py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs font-medium disabled:opacity-50">
          {saving ? "Actualizando..." : "Actualizar sueldo mínimo"}
        </button>
        <p className="text-slate-500 text-[11px] leading-relaxed">
          Actualiza el mínimo usado para IPS y ajusta el sueldo base de empleados que cobraban el mínimo anterior.
        </p>
      </div>
      {historial.length > 0 && (
        <div className="bg-arandu-dark-light border border-white/10 rounded-xl p-4">
          <p className="text-slate-400 text-xs mb-3 font-medium">Historial</p>
          <div className="space-y-2">
            {historial.slice(0, 8).map((h, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-slate-400">{h.fecha_vigencia || h.fecha}</span>
                <span className="text-white font-mono">₲ {(h.valor||0).toLocaleString("es-PY")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function CatastroPage() {
  const { token, user, activeEmpresaPropia } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const logo = activeEmpresaPropia?.slug;

  const [tab, setTab]               = useState("todos");
  const [entidades, setEntidades]   = useState([]);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState("");
  const [showModal, setShowModal]         = useState(false);
  const [editing, setEditing]             = useState(null);
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [showSueldoModal, setShowSueldoModal] = useState(false);
  const [showLiquidarModal, setShowLiquidarModal] = useState(false);
  const [liquidarEmp, setLiquidarEmp] = useState(null);
  const [formLiquidar, setFormLiquidar] = useState({
    fecha_egreso: hoyISO(),
    monto_liquidacion: "",
    motivo: "",
    notas: "",
  });
  const [savingLiquidar, setSavingLiquidar] = useState(false);
  const [prestamosLiquidar, setPrestamosLiquidar] = useState({ loading: false, items: [], error: "" });

  // Auto-abrir modal si viene con ?nuevo=... desde acceso directo del dashboard
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam && [...TABS.map(t => t.key), "localidades", "config"].includes(tabParam)) {
      setTab(tabParam);
    }
    const nuevo = params.get("nuevo");
    if (!nuevo) return;
    // Construir form con rol pre-marcado según el parámetro
    const rolMap = { cliente: "es_cliente", proveedor: "es_proveedor", empleado: "es_empleado" };
    if (rolMap[nuevo]) {
      setEditing({ _presetRol: rolMap[nuevo] });
    } else {
      setEditing(null); // "nuevo=1" o cualquier valor → form en blanco
    }
    setShowModal(true);
    // Limpiar el query param de la URL sin recargar la página
    navigate(location.pathname, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAdmin    = user?.role === "admin" || user?.role === "super_admin" || user?.role === "gerente";
  const hasPerm    = (p) => user?.permisos?.includes(p);
  // El rol Chofer solo se ofrece si la empresa tiene habilitado el módulo
  // Transporte — a diferencia de los demás roles, esto NO se bypasea con
  // isAdmin: si el módulo está apagado para la empresa, no tiene sentido
  // ofrecerlo ni para admin.
  const transporteHabilitado = empresaTieneModulo(activeEmpresaPropia, "transporte");
  const importacionesHabilitado = empresaTieneModulo(activeEmpresaPropia, "importaciones");   // rol Despachante
  const allowedRoles = useMemo(() => ({
    es_cliente: isAdmin || hasPerm("empresas.ver"),
    es_proveedor: isAdmin || hasPerm("proveedores.ver"),
    es_empleado: isAdmin || hasPerm("empleados.ver"),
    es_chofer: transporteHabilitado && (isAdmin || hasPerm("transporte.ver")),
    es_vendedor: isAdmin || hasPerm("vendedores.ver"),
    es_repartidor: isAdmin || hasPerm("facturas.imprimir_delivery"),
    es_despachante: importacionesHabilitado && (isAdmin || hasPerm("importaciones.ver")),
  }), [isAdmin, user?.permisos, transporteHabilitado, importacionesHabilitado]);
  const canVerEntidades = allowedRoles.es_cliente || allowedRoles.es_proveedor || allowedRoles.es_empleado;
  const canCreate  = isAdmin || hasPerm("entidades.crear");
  const canEdit    = isAdmin || hasPerm("entidades.editar");
  const canDelete  = isAdmin || hasPerm("entidades.eliminar");
  const canLiquidarEmpleado = isAdmin || hasPerm("sueldos.liquidar");
  const canVerLoc      = isAdmin || hasPerm("localidades.ver");
  const canCrearLoc    = isAdmin || hasPerm("localidades.crear");
  const canEditarLoc   = isAdmin || hasPerm("localidades.editar");
  const canEliminarLoc = isAdmin || hasPerm("localidades.eliminar");
  const canConfigSueldos = isAdmin || hasPerm("configuracion_catastro.gestionar_sueldos");
  const canConfigHuella = isAdmin || hasPerm("configuracion_catastro.gestionar_huella");
  const canConfig = canConfigSueldos || canConfigHuella;

  // ── Estado localidades ────────────────────────────────────────────────────
  const [localidades, setLocalidades]           = useState([]);
  const [loadingLoc, setLoadingLoc]             = useState(false);
  const [expandedPais, setExpandedPais]         = useState({});
  const [expandedDep, setExpandedDep]           = useState({});
  const [expandedCiudad, setExpandedCiudad]     = useState({});
  const [locModal, setLocModal]                 = useState(null);
  const [locFormVal, setLocFormVal]             = useState("");
  const [locFormVal2, setLocFormVal2]           = useState("");

  const fetchEntidades = useCallback(async () => {
    if (!canVerEntidades) {
      setEntidades([]);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (logo) params.set("logo_tipo", logo);
      const tabCfg = TABS.find(t => t.key === tab);
      if (tabCfg) Object.entries(tabCfg.filter).forEach(([k,v]) => params.set(k, v));
      if (mostrarInactivos) params.set("activo", "false");
      const res = await fetch(`${API}/admin/entidades?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      const ct = res.headers.get("content-type") || "";
      if (res.ok && ct.includes("application/json")) {
        setEntidades(await res.json());
      } else if (res.ok) {
        // respuesta OK pero no es JSON → problema de caché o proxy
        console.error("[CatastroPage] respuesta no-JSON, content-type:", ct);
        toast.error("Respuesta inválida del servidor. Limpiá el caché del navegador (Ctrl+Shift+R).");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(`Error ${res.status}: ${err.detail || "No se pudo cargar el catastro"}`);
      }
    } catch (err) {
      console.error("[CatastroPage] fetchEntidades error:", err);
      toast.error("Error de conexión al cargar catastro");
    }
    finally { setLoading(false); }
  }, [token, logo, tab, mostrarInactivos, canVerEntidades]);

  useEffect(() => { fetchEntidades(); }, [fetchEntidades]);
  useEffect(() => {
    if (canVerEntidades) {
      if (tab === "clientes" && !allowedRoles.es_cliente) setTab("todos");
      if (tab === "proveedores" && !allowedRoles.es_proveedor) setTab("todos");
      if (tab === "empleados" && !allowedRoles.es_empleado) setTab("todos");
      return;
    }
    if (canVerLoc) setTab("localidades");
    else if (canConfig) setTab("config");
  }, [canVerEntidades, canVerLoc, canConfig, tab, allowedRoles]);

  // ── Localidades CRUD ────────────────────────────────────────────────────────
  useEffect(() => {
    if (tab === "localidades" && canVerLoc) fetchLocalidades();
  }, [tab]); // eslint-disable-line

  const fetchLocalidades = async () => {
    setLoadingLoc(true);
    try {
      const res = await fetch(`${API}/admin/localidades/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setLocalidades(await res.json());
    } catch (_) {}
    finally { setLoadingLoc(false); }
  };

  const handleRefreshManual = () => {
    if (tab === "localidades") {
      fetchLocalidades();
      return;
    }
    if (tab !== "config") fetchEntidades();
  };

  const handleLocSubmit = async () => {
    if (!locFormVal.trim()) { toast.error("El valor es requerido"); return; }
    const m = locModal;
    try {
      let url, method, body;
      if (m.type === "pais" && m.mode === "crear") {
        url = `${API}/admin/localidades/`; method = "POST";
        body = { cPais: locFormVal2.trim().toUpperCase(), dDesPais: locFormVal.trim() };
      } else if (m.type === "pais" && m.mode === "editar") {
        url = `${API}/admin/localidades/${m.pais.cPais}`; method = "PUT";
        body = { dDesPais: locFormVal.trim() };
      } else if (m.type === "dep" && m.mode === "crear") {
        url = `${API}/admin/localidades/${m.pais.cPais}/departamentos`; method = "POST";
        body = { cDep: locFormVal2.trim(), dDesDep: locFormVal.trim() };
      } else if (m.type === "dep" && m.mode === "editar") {
        url = `${API}/admin/localidades/${m.pais.cPais}/departamentos/${m.dep.cDep}`; method = "PUT";
        body = { dDesDep: locFormVal.trim() };
      } else if (m.type === "ciudad" && m.mode === "crear") {
        url = `${API}/admin/localidades/${m.pais.cPais}/departamentos/${m.dep.cDep}/ciudades`; method = "POST";
        body = { cCiu: Number(locFormVal2), dDesCiu: locFormVal.trim() };
      } else if (m.type === "ciudad" && m.mode === "editar") {
        url = `${API}/admin/localidades/${m.pais.cPais}/departamentos/${m.dep.cDep}/ciudades/${m.ciudad.cCiu}`; method = "PUT";
        body = { dDesCiu: locFormVal.trim() };
      } else if (m.type === "localidad" && m.mode === "crear") {
        url = `${API}/admin/localidades/${m.pais.cPais}/departamentos/${m.dep.cDep}/ciudades/${m.ciudad.cCiu}/localidades`; method = "POST";
        body = { cLoc: Number(locFormVal2), dDesLoc: locFormVal.trim() };
      } else if (m.type === "localidad" && m.mode === "editar") {
        url = `${API}/admin/localidades/${m.pais.cPais}/departamentos/${m.dep.cDep}/ciudades/${m.ciudad.cCiu}/localidades/${m.localidad.cLoc}`; method = "PUT";
        body = { cLoc: m.localidad.cLoc, dDesLoc: locFormVal.trim() };
      }
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success("Guardado");
        setLocModal(null); setLocFormVal(""); setLocFormVal2("");
        fetchLocalidades();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error al guardar");
      }
    } catch (_) { toast.error("Error de conexión"); }
  };

  const handleLocDelete = async (type, pais, dep, ciudad, localidad) => {
    const labels = { pais: "país", dep: "departamento", ciudad: "distrito", localidad: "localidad" };
    if (!window.confirm(`¿Eliminar este ${labels[type]}?`)) return;
    try {
      let url;
      if (type === "pais") url = `${API}/admin/localidades/${pais.cPais}`;
      else if (type === "dep") url = `${API}/admin/localidades/${pais.cPais}/departamentos/${dep.cDep}`;
      else if (type === "ciudad") url = `${API}/admin/localidades/${pais.cPais}/departamentos/${dep.cDep}/ciudades/${ciudad.cCiu}`;
      else url = `${API}/admin/localidades/${pais.cPais}/departamentos/${dep.cDep}/ciudades/${ciudad.cCiu}/localidades/${localidad.cLoc}`;
      const res = await fetch(url, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success("Eliminado"); fetchLocalidades(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Error al eliminar"); }
    } catch (_) { toast.error("Error de conexión"); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entidades;
    return entidades.filter(e =>
      e.nombre?.toLowerCase().includes(q) ||
      e.ruc?.toLowerCase().includes(q) ||
      e.email?.toLowerCase().includes(q) ||
      e.ciudad?.toLowerCase().includes(q)
    );
  }, [entidades, search]);

  const handleToggleActivo = async (ent) => {
    try {
      const res = await fetch(`${API}/admin/entidades/${ent.id}/toggle-activo`, {
        method: "PATCH", headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success(ent.activo ? "Entidad desactivada" : "Entidad activada"); fetchEntidades(); }
      else toast.error("Error al cambiar estado");
    } catch { toast.error("Error de conexión"); }
  };

  const handleDelete = async (ent) => {
    if (!window.confirm(`¿Eliminar "${ent.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      const res = await fetch(`${API}/admin/entidades/${ent.id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success("Entidad eliminada"); fetchEntidades(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Error al eliminar"); }
    } catch { toast.error("Error de conexión"); }
  };

  const fetchPrestamosLiquidar = async (ent) => {
    if (!ent?.id) return;
    setPrestamosLiquidar({ loading: true, items: [], error: "" });
    try {
      const params = new URLSearchParams({ tipo: "funcionario", estado: "activo" });
      if (logo) params.set("empresa_id", logo);
      const res = await fetch(`${API}/admin/prestamos?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      if (!res.ok) {
        throw new Error(await readApiError(res, "No se pudo revisar préstamos vigentes"));
      }
      const data = await res.json();
      const items = (data || []).filter(p => (p.entidad_id || p.empleado_id) === ent.id);
      setPrestamosLiquidar({ loading: false, items, error: "" });
    } catch (e) {
      setPrestamosLiquidar({ loading: false, items: [], error: e.message || "No se pudo revisar préstamos vigentes" });
    }
  };

  const openLiquidarEmpleado = (ent) => {
    setLiquidarEmp(ent);
    setFormLiquidar({
      fecha_egreso: ent.fecha_egreso || hoyISO(),
      monto_liquidacion: ent.monto_liquidacion || "",
      motivo: ent.motivo_liquidacion || "",
      notas: ent.notas_liquidacion || "",
    });
    setPrestamosLiquidar({ loading: false, items: [], error: "" });
    setShowLiquidarModal(true);
    fetchPrestamosLiquidar(ent);
  };

  const handleLiquidarEmpleado = async () => {
    if (!liquidarEmp?.id) return;
    if (!formLiquidar.fecha_egreso) {
      toast.error("Fecha de egreso requerida");
      return;
    }
    const monto = Number(formLiquidar.monto_liquidacion || 0);
    if (Number.isNaN(monto) || monto < 0) {
      toast.error("Monto de liquidación inválido");
      return;
    }
    const nombre = [liquidarEmp.nombre, liquidarEmp.apellido].filter(Boolean).join(" ");
    if (!window.confirm(`¿Liquidar y desvincular a ${nombre || "este empleado"}? Ya no aparecerá como sueldo pendiente.`)) return;
    setSavingLiquidar(true);
    try {
      const res = await fetch(`${API}/admin/empleados/${liquidarEmp.id}/liquidar`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...formLiquidar,
          monto_liquidacion: monto,
        }),
      });
      if (!res.ok) {
        throw new Error(await readApiError(res, "Error al liquidar empleado"));
      }
      const actualizado = await res.json();
      setEntidades(prev => prev.map(e => e.id === actualizado.id ? actualizado : e));
      toast.success("Empleado liquidado y desvinculado");
      setShowLiquidarModal(false);
      setLiquidarEmp(null);
      setPrestamosLiquidar({ loading: false, items: [], error: "" });
      fetchEntidades();
    } catch (e) {
      toast.error(e.message || "Error al liquidar empleado");
    } finally {
      setSavingLiquidar(false);
    }
  };

  return (
    <div className="min-h-screen bg-arandu-dark text-white">
      {/* Header */}
      <div className="bg-arandu-dark-light border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5"/>
            </button>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-400"/>
              <h1 className="text-lg font-semibold">Catastro de Entidades</h1>
            </div>
          </div>
          <button
            onClick={handleRefreshManual}
            disabled={loading || loadingLoc || tab === "config"}
            className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white disabled:opacity-50 flex items-center justify-center transition-colors"
            title="Actualizar"
          >
            <RefreshCw className={`w-4 h-4 ${(loading || loadingLoc) ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-1 bg-white/5 rounded-xl p-1 mb-6 w-fit">
          {[...(canVerEntidades ? TABS.filter(t =>
              t.key === "todos" ||
              (t.key === "clientes" && allowedRoles.es_cliente) ||
              (t.key === "proveedores" && allowedRoles.es_proveedor) ||
              (t.key === "empleados" && allowedRoles.es_empleado)
            ) : []),
            ...(canVerLoc ? [{ key: "localidades", label: "Localidades", icon: Globe, filter: {} }] : []),
            ...(canConfig ? [{ key: "config", label: "Configuración", icon: Settings, filter: {} }] : [])
          ].map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  tab === t.key
                    ? "bg-violet-600 text-white shadow"
                    : "text-slate-400 hover:text-white"
                }`}>
                <Icon className="w-4 h-4"/>{t.label}
              </button>
            );
          })}
        </div>

        {/* Localidades tab */}
        {tab === "localidades" && canVerLoc && (
          <>
            <div className="flex items-center justify-between mb-5">
              <p className="text-slate-400 text-sm">Catálogo de países, departamentos y distritos.</p>
              {canCrearLoc && (
                <button onClick={() => { setLocFormVal(""); setLocFormVal2(""); setLocModal({ type: "pais", mode: "crear" }); }}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm font-medium text-white">
                  <Plus className="w-4 h-4"/> Nuevo País
                </button>
              )}
            </div>

            {/* Modal localidades */}
            {locModal && (
              <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
                <div className="bg-arandu-dark-light border border-white/10 rounded-2xl p-6 w-full max-w-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-white">
                      {locModal.mode === "crear" ? "Agregar" : "Editar"}{" "}
                      {locModal.type === "pais" ? "País" : locModal.type === "dep" ? "Departamento" : locModal.type === "ciudad" ? "Distrito" : "Localidad"}
                    </h2>
                    <button onClick={() => setLocModal(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5"/></button>
                  </div>
                  <div className="space-y-3">
                    {locModal.mode === "crear" && (locModal.type === "pais" || locModal.type === "dep") && (
                      <div>
                        <label className="block text-slate-400 text-xs mb-1">
                          {locModal.type === "pais" ? "Código país (3 letras, ej: PRY)" : "Código dep. (2 dígitos, ej: 01)"}
                        </label>
                        <input value={locFormVal2} onChange={e => setLocFormVal2(e.target.value)}
                          className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none"
                          placeholder={locModal.type === "pais" ? "PRY" : "01"} maxLength={locModal.type === "pais" ? 3 : 2}/>
                      </div>
                    )}
                    {locModal.mode === "crear" && (locModal.type === "ciudad" || locModal.type === "localidad") && (
                      <div>
                        <label className="block text-slate-400 text-xs mb-1">Código (número)</label>
                        <input type="number" value={locFormVal2} onChange={e => setLocFormVal2(e.target.value)}
                          className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                          placeholder="1"/>
                      </div>
                    )}
                    <div>
                      <label className="block text-slate-400 text-xs mb-1">Nombre</label>
                      <input value={locFormVal} onChange={e => setLocFormVal(e.target.value)} autoFocus
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                        placeholder="Nombre..." onKeyDown={e => e.key === "Enter" && handleLocSubmit()}/>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button onClick={() => setLocModal(null)}
                        className="flex-1 py-2 border border-white/20 rounded-lg text-slate-300 text-sm hover:bg-white/5">Cancelar</button>
                      <button onClick={handleLocSubmit}
                        className="flex-1 flex items-center justify-center gap-2 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-white text-sm font-medium">
                        <Save className="w-4 h-4"/>{locModal.mode === "crear" ? "Agregar" : "Guardar"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {loadingLoc ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"/>
              </div>
            ) : localidades.length === 0 ? (
              <div className="text-center py-12 bg-arandu-dark-light border border-white/5 rounded-xl">
                <Globe className="w-12 h-12 text-slate-700 mx-auto mb-3"/>
                <p className="text-slate-400">Sin localidades registradas</p>
              </div>
            ) : (
              <div className="space-y-2">
                {localidades.map(pais => (
                  <div key={pais.cPais} className="bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden">
                    {/* País */}
                    <div className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] cursor-pointer select-none"
                      onClick={() => setExpandedPais(p => ({ ...p, [pais.cPais]: !p[pais.cPais] }))}>
                      <Globe className="w-4 h-4 text-emerald-400 shrink-0"/>
                      <span className="text-white font-medium flex-1">{pais.dDesPais}</span>
                      <span className="text-slate-600 text-xs font-mono">{pais.cPais}</span>
                      <span className="text-slate-500 text-xs">{pais.departamentos?.length || 0} dep.</span>
                      <AuditoriaRegistroBtn registro={pais} />
                      {canEditarLoc && (
                        <button onClick={e => { e.stopPropagation(); setLocFormVal(pais.dDesPais); setLocFormVal2(""); setLocModal({ type: "pais", mode: "editar", pais }); }}
                          className="text-slate-500 hover:text-white p-1"><Edit className="w-3.5 h-3.5"/></button>
                      )}
                      {canEliminarLoc && (
                        <button onClick={e => { e.stopPropagation(); handleLocDelete("pais", pais); }}
                          className="text-slate-500 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5"/></button>
                      )}
                      {canCrearLoc && (
                        <button onClick={e => { e.stopPropagation(); setLocFormVal(""); setLocFormVal2(""); setLocModal({ type: "dep", mode: "crear", pais }); }}
                          className="text-slate-500 hover:text-emerald-400 p-1"><Plus className="w-3.5 h-3.5"/></button>
                      )}
                      {expandedPais[pais.cPais] ? <ChevronDown className="w-4 h-4 text-slate-500"/> : <ChevronRight className="w-4 h-4 text-slate-500"/>}
                    </div>

                    {/* Departamentos */}
                    {expandedPais[pais.cPais] && (pais.departamentos || []).map(dep => (
                      <div key={dep.cDep} className="border-t border-white/5">
                        <div className="flex items-center gap-3 px-6 py-2.5 hover:bg-white/[0.02] cursor-pointer select-none"
                          onClick={() => setExpandedDep(d => ({ ...d, [`${pais.cPais}-${dep.cDep}`]: !d[`${pais.cPais}-${dep.cDep}`] }))}>
                          <span className="w-3 h-px bg-slate-700 shrink-0"/>
                          <span className="text-slate-300 text-sm flex-1">{dep.dDesDep}</span>
                          <span className="text-slate-600 text-xs font-mono">{dep.cDep}</span>
                          <span className="text-slate-500 text-xs">{dep.ciudades?.length || 0} distritos</span>
                          {canEditarLoc && (
                            <button onClick={e => { e.stopPropagation(); setLocFormVal(dep.dDesDep); setLocFormVal2(""); setLocModal({ type: "dep", mode: "editar", pais, dep }); }}
                              className="text-slate-600 hover:text-white p-1"><Edit className="w-3 h-3"/></button>
                          )}
                          {canEliminarLoc && (
                            <button onClick={e => { e.stopPropagation(); handleLocDelete("dep", pais, dep); }}
                              className="text-slate-600 hover:text-red-400 p-1"><Trash2 className="w-3 h-3"/></button>
                          )}
                          {canCrearLoc && (
                            <button onClick={e => { e.stopPropagation(); setLocFormVal(""); setLocFormVal2(""); setLocModal({ type: "ciudad", mode: "crear", pais, dep }); }}
                              className="text-slate-600 hover:text-emerald-400 p-1"><Plus className="w-3 h-3"/></button>
                          )}
                          {expandedDep[`${pais.cPais}-${dep.cDep}`] ? <ChevronDown className="w-3.5 h-3.5 text-slate-600"/> : <ChevronRight className="w-3.5 h-3.5 text-slate-600"/>}
                        </div>

                        {/* Distritos */}
                        {expandedDep[`${pais.cPais}-${dep.cDep}`] && (
                          <div className="bg-black/20 divide-y divide-white/[0.03]">
                            {(dep.ciudades || []).map(ciudad => {
                              const ck = `${pais.cPais}-${dep.cDep}-${ciudad.cCiu}`;
                              const locCount = ciudad.localidades?.length || 0;
                              return (
                                <div key={ciudad.cCiu}>
                                  <div className="flex items-center gap-2 px-10 py-2 hover:bg-white/[0.02] cursor-pointer select-none"
                                    onClick={() => locCount > 0 && setExpandedCiudad(c => ({ ...c, [ck]: !c[ck] }))}>
                                    <MapPin className="w-3 h-3 text-slate-600 shrink-0"/>
                                    <span className="text-slate-400 text-xs flex-1">{ciudad.dDesCiu}</span>
                                    <span className="text-slate-700 text-xs font-mono mr-1">{ciudad.cCiu}</span>
                                    {locCount > 0 && <span className="text-slate-600 text-xs">{locCount} loc.</span>}
                                    {canCrearLoc && (
                                      <button onClick={e => { e.stopPropagation(); setLocFormVal(""); setLocFormVal2(""); setLocModal({ type: "localidad", mode: "crear", pais, dep, ciudad }); }}
                                        className="text-slate-700 hover:text-emerald-400 p-0.5"><Plus className="w-2.5 h-2.5"/></button>
                                    )}
                                    {canEditarLoc && (
                                      <button onClick={e => { e.stopPropagation(); setLocFormVal(ciudad.dDesCiu); setLocFormVal2(String(ciudad.cCiu)); setLocModal({ type: "ciudad", mode: "editar", pais, dep, ciudad }); }}
                                        className="text-slate-700 hover:text-white p-0.5"><Edit className="w-2.5 h-2.5"/></button>
                                    )}
                                    {canEliminarLoc && (
                                      <button onClick={e => { e.stopPropagation(); handleLocDelete("ciudad", pais, dep, ciudad); }}
                                        className="text-slate-700 hover:text-red-400 p-0.5"><Trash2 className="w-2.5 h-2.5"/></button>
                                    )}
                                    {locCount > 0 && (expandedCiudad[ck] ? <ChevronDown className="w-3 h-3 text-slate-700"/> : <ChevronRight className="w-3 h-3 text-slate-700"/>)}
                                  </div>
                                  {expandedCiudad[ck] && (
                                    <div className="bg-black/30 divide-y divide-white/[0.02]">
                                      {(ciudad.localidades || []).map(loc => (
                                        <div key={loc.cLoc} className="flex items-center gap-2 px-14 py-1.5">
                                          <span className="w-2 h-px bg-slate-800 shrink-0"/>
                                          <span className="text-slate-500 text-xs flex-1">{loc.dDesLoc}</span>
                                          <span className="text-slate-700 text-xs font-mono mr-1">{loc.cLoc}</span>
                                          {canEditarLoc && (
                                            <button onClick={() => { setLocFormVal(loc.dDesLoc); setLocFormVal2(""); setLocModal({ type: "localidad", mode: "editar", pais, dep, ciudad, localidad: loc }); }}
                                              className="text-slate-800 hover:text-white p-0.5"><Edit className="w-2 h-2"/></button>
                                          )}
                                          {canEliminarLoc && (
                                            <button onClick={() => handleLocDelete("localidad", pais, dep, ciudad, loc)}
                                              className="text-slate-800 hover:text-red-400 p-0.5"><Trash2 className="w-2 h-2"/></button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {(dep.ciudades || []).length === 0 && (
                              <p className="text-slate-600 text-xs px-10 py-2">Sin distritos registrados</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Config tab */}
        {tab === "config" && canConfig && (
          <div className="space-y-4 max-w-2xl">
            <p className="text-slate-400 text-sm">Configuración del módulo Catastro de Entidades.</p>

            {/* Card: Lector de huella digital (por empresa) */}
            {canConfigHuella && (
              <div className="border border-white/10 rounded-xl p-5 bg-white/3">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                    <Settings className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Lector de huella digital</p>
                    <p className="text-slate-400 text-xs mt-0.5 max-w-md">
                      Elegí el dispositivo que usa esta empresa para capturar la huella de clientes y demás entidades.
                    </p>
                  </div>
                </div>
                <HuellaConfigPanel token={token} logo={logo} canEdit={canConfigHuella} />
              </div>
            )}

            {/* Card: Configuración de empleados */}
            {canConfigSueldos && (
            <div className="border border-white/10 rounded-xl p-5 bg-white/3">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-violet-500/15 flex items-center justify-center flex-shrink-0">
                    <UserCheck className="w-4 h-4 text-violet-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Sueldo mínimo vigente</p>
                    <p className="text-slate-400 text-xs mt-0.5 max-w-sm">
                      Actualizá el sueldo mínimo legal. Al cambiar el valor podés recalcular IPS y sueldos base de los empleados que cobran el mínimo.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSueldoModal(true)}
                  className="h-8 text-xs bg-violet-600/80 hover:bg-violet-600 text-white px-3 rounded-lg flex items-center gap-1.5 flex-shrink-0 ml-4 transition-colors"
                >
                  <Settings className="w-3.5 h-3.5" /> Gestionar
                </button>
              </div>
            </div>
            )}
          </div>
        )}

        {/* Modal: Sueldo mínimo */}
        {showSueldoModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowSueldoModal(false)} />
            <div className="relative bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-violet-500/15 flex items-center justify-center">
                    <UserCheck className="w-4 h-4 text-violet-400" />
                  </div>
                  <p className="text-white font-semibold text-sm">Sueldo mínimo vigente</p>
                </div>
                <button onClick={() => setShowSueldoModal(false)}
                  className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-6">
                <SueldoMinimoPanel token={token} logo={logo} />
              </div>
            </div>
          </div>
        )}

        {/* List tabs */}
        {tab !== "config" && tab !== "localidades" && canVerEntidades && (
          <>
            {/* Barra de acciones */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500"/>
                <input
                  value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Buscar por nombre, RUC, email..."
                  className="w-full pl-9 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-violet-500/60"/>
              </div>
              <button onClick={() => setMostrarInactivos(v => !v)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                  mostrarInactivos
                    ? "bg-orange-600/20 border-orange-500/40 text-orange-300"
                    : "border-white/10 text-slate-400 hover:text-white bg-white/5"
                }`}>
                {mostrarInactivos ? <ToggleRight className="w-4 h-4"/> : <ToggleLeft className="w-4 h-4"/>}
                Inactivos
              </button>
              {canCreate && (
                <button onClick={() => { setEditing(null); setShowModal(true); }}
                  className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 rounded-lg text-sm font-medium text-white">
                  <Plus className="w-4 h-4"/> Nueva entidad
                </button>
              )}
            </div>

            {/* Contador */}
            <p className="text-slate-500 text-xs mb-3">{filtered.length} entidad{filtered.length !== 1 ? "es" : ""}</p>

            {/* Tabla */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin"/>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-12 h-12 text-slate-600 mx-auto mb-3"/>
                <p className="text-slate-400">
                  {search ? "Sin resultados para esa búsqueda" : "No hay entidades registradas"}
                </p>
                {canCreate && !search && (
                  <button onClick={() => { setEditing(null); setShowModal(true); }}
                    className="mt-3 px-4 py-2 bg-violet-600 hover:bg-violet-700 rounded-lg text-sm text-white">
                    Crear la primera
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                {filtered.map(ent => (
                  <div key={ent.id}
                    className={`bg-arandu-dark-light border rounded-xl px-4 py-3 flex items-center justify-between gap-4 transition-colors ${
                      ent.activo === false
                        ? "border-white/5 opacity-50"
                        : "border-white/10 hover:border-white/20"
                    }`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-medium text-sm truncate">{ent.nombre}</span>
                        <div className="flex items-center gap-1 flex-wrap">
                          {ent.es_cliente   && badge("Cliente",    "blue")}
                          {ent.es_proveedor && badge("Proveedor",  "green")}
                          {ent.es_chofer    && badge("Chofer",     "amber")}
                          {ent.es_vendedor  && badge("Vendedor",   "cyan")}
                          {ent.es_repartidor && badge("Repartidor", "orange")}
                          {ent.es_despachante && badge("Despachante", "rose")}
                          {ent.es_entidad_bancaria && badge("Banco/financiera", "cyan")}
                          {ent.es_empleado && ent.logo_tipo && logo && ent.logo_tipo !== logo && badge(ent.logo_tipo, "cyan")}
                          {ent.es_empleado && (ent.liquidado || ent.estado_laboral === "liquidado")
                            ? badge("Empleado liquidado", "red")
                            : ent.es_empleado && badge("Empleado", "violet")}
                          {ent.activo === false && badge("Inactivo", "red")}
                          {(() => {
                            const dias = diasHastaCumpleanos(ent.fecha_nacimiento);
                            if (dias === null || dias > 5) return null;
                            return (
                              <span title={dias === 0 ? "¡Hoy es su cumpleaños!" : `Cumpleaños en ${dias} día${dias !== 1 ? "s" : ""}`}
                                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border font-medium ${
                                  dias === 0
                                    ? "bg-pink-600/30 border-pink-500/60 text-pink-200"
                                    : "bg-rose-600/20 border-rose-500/40 text-rose-300"
                                }`}>
                                <Cake className="w-3 h-3"/>
                                {dias === 0 ? "¡Hoy!" : `${dias}d`}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        {ent.ruc      && <span className="text-slate-500 text-xs">RUC: {ent.ruc}</span>}
                        {ent.telefono && <span className="text-slate-500 text-xs">{ent.telefono}</span>}
                        {ent.email    && <span className="text-slate-500 text-xs">{ent.email}</span>}
                        {(ent.ddes_dis || ent.ciudad) && <span className="text-slate-500 text-xs">{ent.ddes_dis || ent.ciudad}{ent.ddes_dep ? `, ${ent.ddes_dep}` : ""}</span>}
                        {ent.tipo_contribuyente && ent.tipo_contribuyente !== "juridica" && (
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${
                            ent.tipo_contribuyente === "fisica"
                              ? "bg-sky-600/20 border-sky-500/40 text-sky-300"
                              : ent.tipo_contribuyente === "extranjero"
                              ? "bg-orange-600/20 border-orange-500/40 text-orange-300"
                              : "bg-slate-600/20 border-slate-500/40 text-slate-400"
                          }`}>
                            {ent.tipo_contribuyente === "fisica" ? "Pers. Física"
                              : ent.tipo_contribuyente === "extranjero" ? "Extranjero"
                              : "No contribuyente"}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <AuditoriaRegistroBtn registro={ent} />
                      {ent.es_empleado && ent.activo !== false && !ent.liquidado && canLiquidarEmpleado && (
                        <button
                          onClick={() => openLiquidarEmpleado(ent)}
                          title="Liquidar empleado"
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-300 hover:bg-red-500/10 transition-all">
                          <UserX className="w-4 h-4"/>
                        </button>
                      )}
                      {(() => {
                        const rolesEnt = ["es_cliente", "es_proveedor", "es_empleado"].filter(k => ent[k]);
                        const canManageEnt = rolesEnt.every(k => allowedRoles[k]);
                        return canEdit && canManageEnt;
                      })() && (
                        <button onClick={() => handleToggleActivo(ent)} title={ent.activo === false ? "Activar" : "Desactivar"}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-all">
                          {ent.activo === false ? <ToggleLeft className="w-4 h-4"/> : <ToggleRight className="w-4 h-4 text-green-400"/>}
                        </button>
                      )}
                      {(() => {
                        const rolesEnt = ["es_cliente", "es_proveedor", "es_empleado"].filter(k => ent[k]);
                        const canManageEnt = rolesEnt.every(k => allowedRoles[k]);
                        return canEdit && canManageEnt;
                      })() && (
                        <button onClick={() => { setEditing(ent); setShowModal(true); }}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-all">
                          <Edit2 className="w-4 h-4"/>
                        </button>
                      )}
                      {(() => {
                        const rolesEnt = ["es_cliente", "es_proveedor", "es_empleado"].filter(k => ent[k]);
                        const canManageEnt = rolesEnt.every(k => allowedRoles[k]);
                        return canDelete && canManageEnt;
                      })() && (
                        <button onClick={() => handleDelete(ent)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all">
                          <Trash2 className="w-4 h-4"/>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showLiquidarModal && liquidarEmp && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-500/15 text-red-300 flex items-center justify-center">
                  <UserX className="w-5 h-5"/>
                </div>
                <div>
                  <h2 className="text-white font-heading text-lg">Liquidar empleado</h2>
                  <p className="text-slate-400 text-sm">{liquidarEmp.nombre} {liquidarEmp.apellido}</p>
                </div>
              </div>
              <button
                disabled={savingLiquidar}
                onClick={() => setShowLiquidarModal(false)}
                className="text-slate-400 hover:text-white disabled:opacity-50">
                <X className="w-5 h-5"/>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-amber-500/10 border border-amber-500/25 rounded-xl p-3 text-sm text-amber-100">
                Al confirmar, el empleado queda inactivo y no volverá a generar sueldo pendiente. Los sueldos ya pagados quedan como historial.
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-slate-400 text-sm">Fecha de egreso *</label>
                  <DateInput
                    value={formLiquidar.fecha_egreso}
                    onChange={e => setFormLiquidar(f => ({ ...f, fecha_egreso: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 mt-1 focus:outline-none focus:border-red-400"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-sm">Monto de liquidación</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={formLiquidar.monto_liquidacion}
                    onChange={e => setFormLiquidar(f => ({ ...f, monto_liquidacion: e.target.value }))}
                    placeholder="0"
                    className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 mt-1 focus:outline-none focus:border-red-400"
                  />
                  <p className="text-slate-500 text-xs mt-1">
                    {formatoMontoLiquidacion(formLiquidar.monto_liquidacion, liquidarEmp.moneda_sueldo || liquidarEmp.moneda || "PYG")}
                  </p>
                </div>
              </div>
              {prestamosLiquidar.loading ? (
                <div className="bg-slate-500/10 border border-white/10 rounded-xl p-3 text-sm text-slate-300">
                  Revisando préstamos vigentes...
                </div>
              ) : prestamosLiquidar.error ? (
                <div className="bg-amber-500/10 border border-amber-500/25 rounded-xl p-3 text-sm text-amber-100">
                  No se pudo revisar si el empleado tiene préstamos vigentes. Revisá el módulo financiero antes de confirmar.
                </div>
              ) : prestamosLiquidar.items.length > 0 ? (
                <div className="bg-amber-500/10 border border-amber-500/25 rounded-xl p-3 text-sm text-amber-100">
                  Este empleado cuenta con {prestamosLiquidar.items.length} préstamo{prestamosLiquidar.items.length !== 1 ? "s" : ""} vigente{prestamosLiquidar.items.length !== 1 ? "s" : ""} con la empresa. No olvides revisarlo antes de cerrar la liquidación.
                </div>
              ) : (
                <div className="bg-emerald-500/10 border border-emerald-500/25 rounded-xl p-3 text-sm text-emerald-100">
                  Este empleado no cuenta con préstamos vigentes con la empresa.
                </div>
              )}
              <div>
                <label className="text-slate-400 text-sm">Motivo</label>
                <input
                  value={formLiquidar.motivo}
                  onChange={e => setFormLiquidar(f => ({ ...f, motivo: e.target.value }))}
                  placeholder="Ej: Renuncia, fin de contrato, desvinculación..."
                  className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 mt-1 focus:outline-none focus:border-red-400"
                />
              </div>
              <div>
                <label className="text-slate-400 text-sm">Notas</label>
                <textarea
                  value={formLiquidar.notas}
                  onChange={e => setFormLiquidar(f => ({ ...f, notas: e.target.value }))}
                  rows={3}
                  placeholder="Observaciones de liquidación..."
                  className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2 mt-1 focus:outline-none focus:border-red-400 resize-none"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="button"
                  disabled={savingLiquidar}
                  onClick={() => setShowLiquidarModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/10 disabled:opacity-50">
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={savingLiquidar}
                  onClick={() => imprimirLiquidacionEmpleado(liquidarEmp, formLiquidar, user, logo)}
                  className="flex-1 px-4 py-2 rounded-lg border border-violet-500/35 bg-violet-600/15 text-violet-200 hover:bg-violet-600/25 disabled:opacity-50 flex items-center justify-center gap-2">
                  <Printer className="w-4 h-4"/> Imprimir documento
                </button>
                <button
                  type="button"
                  disabled={savingLiquidar}
                  onClick={handleLiquidarEmpleado}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 flex items-center justify-center gap-2">
                  <UserX className="w-4 h-4"/>
                  {savingLiquidar ? "Liquidando..." : "Liquidar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <EntidadModal
          entidad={editing}
          token={token}
          logo={logo}
          allowedRoles={allowedRoles}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={() => { setShowModal(false); setEditing(null); fetchEntidades(); }}
        />
      )}
    </div>
  );
}
